import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import Icon from '../common/Icon';
import auth from '../../services/authService';
import logo from '../../images/mg_logo.png';
import NavBarMenu from './NavBarMenu';
import { useAppBar } from '../../context/AppBarContext';
import { ReactComponent as UserIcon } from '../../icons/user.svg';
import { ReactComponent as SettingIcon } from '../../icons/settings.svg';
import { ReactComponent as SearchIcon } from '../../icons/search.svg';
import { ReactComponent as SongsIcon } from '../../icons/playlist.svg';
import { ReactComponent as PeopleIcon } from '../../icons/group.svg';
import { ReactComponent as LogOutIcon } from '../../icons/log-out.svg';
import { ReactComponent as StarIcon } from '../../icons/star-solid.svg';
import { SUBSCRIPTION_ENABLED, isSubscribed } from '../../utils/subscription';

import './NavBar.scss';

const NavBar = () => {
  const { t } = useTranslation();
  const [user] = useState(auth.getCurrentUser());
  const [menuOpen, setMenuOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const profileRef = useRef(null);
  const location = useLocation();
  const { setActionsSlot, setTraySlot, setPageSlot, pageMode, pageSticky } = useAppBar();
  // The logo and menu only ever come back from a page, so after the first page they arrive going back
  const leftPage = useRef(false);

  useEffect(() => {
    if (pageMode) leftPage.current = true;
  }, [pageMode]);

  useEffect(() => {
    setMenuOpen(false);
    setProfileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!profileOpen) return undefined;
    const onDocClick = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setProfileOpen(false);
      }
    };
    const onEsc = (e) => {
      if (e.key === 'Escape') setProfileOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, [profileOpen]);

  // The sheet is the phone's menu and the dropdown the desktop's, so neither stays open across the
  // breakpoint. matchMedia rather than resize, so the keyboard opening doesn't close the menu.
  useEffect(() => {
    const mql = window.matchMedia('(min-width: 992px)');

    const onChange = () => {
      setMenuOpen(false);
      setProfileOpen(false);
    };

    mql.addEventListener('change', onChange);

    return () => mql.removeEventListener('change', onChange);
  }, []);

  if (!user || location.pathname === '/signout') return null;

  const showUpgrade = SUBSCRIPTION_ENABLED && user.isAdmin && !isSubscribed(user);

  return (
    <>
      <header className={cx('NavBar', { 'is-sticky': pageSticky })}>
        <div className="bar">
          <div className={cx('capsule', { 'is-page': pageMode })}>
            {/* A page's PageBar portals in here and takes the bar over */}
            <div className="page-slot" ref={setPageSlot} />

            {/* Hidden rather than unmounted, so coming back doesn't rebuild the logo and wait on its decode */}
            <div className={cx('home', { 'enter-back': leftPage.current, 'is-hidden': pageMode })}>
              <Link className="brand" to="/songs">
                <img className="logo" src={logo} alt={t('nav.logoAlt')} />
              </Link>

              <nav className="links" aria-label={t('nav.main')}>
                <NavLink className="link" activeClassName="is-active" to="/songs">
                  <Icon icon={<SongsIcon />} />
                  <span>{t('nav.songs')}</span>
                </NavLink>
                <NavLink className="link" activeClassName="is-active" to="/myteam">
                  <Icon icon={<PeopleIcon />} />
                  <span>{user.teamName}</span>
                </NavLink>
                <NavLink className="link" activeClassName="is-active" to="/searchsongs">
                  <Icon icon={<SearchIcon />} />
                  <span>{t('nav.publicSongs')}</span>
                </NavLink>
              </nav>

              <div className="actions" ref={setActionsSlot} />

              <button
                type="button"
                className="avatar"
                aria-label={t('nav.userMenu', { name: user.firstName })}
                aria-haspopup="dialog"
                aria-expanded={menuOpen}
                onClick={() => setMenuOpen(true)}
              >
                <Icon icon={<UserIcon />} />
              </button>

              <div className="profile" ref={profileRef}>
                <button
                  type="button"
                  className={cx('profile-toggle', { 'is-open': profileOpen })}
                  aria-label={t('nav.userMenu', { name: user.firstName })}
                  aria-haspopup="true"
                  aria-expanded={profileOpen}
                  onClick={() => setProfileOpen((v) => !v)}
                >
                  <span className="avatar-dot" aria-hidden="true">
                    <Icon icon={<UserIcon />} />
                  </span>
                </button>
                <div className={cx('profile-menu', { 'is-open': profileOpen })} role="menu">
                  {showUpgrade && (
                    <NavLink
                      onClick={() => setProfileOpen(false)}
                      className="profile-item profile-item--upgrade"
                      activeClassName="is-active"
                      to="/subscribe"
                    >
                      <Icon icon={<StarIcon />} className="solid" />
                      <span>{t('nav.goUnlimited')}</span>
                    </NavLink>
                  )}
                  <NavLink
                    onClick={() => setProfileOpen(false)}
                    className="profile-item"
                    activeClassName="is-active"
                    to="/settings"
                  >
                    <Icon icon={<SettingIcon />} />
                    <span>{t('nav.settings')}</span>
                  </NavLink>
                  <div className="profile-divider" />
                  <Link onClick={() => setProfileOpen(false)} className="profile-item" to="/signout">
                    <Icon icon={<LogOutIcon />} />
                    <span>{t('nav.signOut')}</span>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>

        <NavBarMenu isOpen={menuOpen} onClose={() => setMenuOpen(false)} user={user} showUpgrade={showUpgrade} />
      </header>

      {/* Beside the header rather than in it, so it can stay stuck to the top after the bar scrolls away */}
      <div className="NavBarTray" ref={setTraySlot} />
    </>
  );
};

export default NavBar;
